import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, AlertTriangle, Loader2, Mail } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";
import { getStripeEnvironment } from "@/lib/stripe";
import { track } from "@/lib/analyticsEvents";
import { toast } from "sonner";

const RESEND_COOLDOWN_SECONDS = 30;

const AuthFinalize = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [message, setMessage] = useState<string>("");
  const [email, setEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      setMessage("Missing checkout session.");
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.functions.invoke("signup-finalize", {
        body: { sessionId, environment: getStripeEnvironment() },
      });
      if (cancelled) return;
      if (error || data?.error) {
        setState("error");
        setMessage(error?.message ?? data?.error ?? "Could not complete signup.");
        return;
      }
      setState("ok");
      if (typeof data?.email === "string") setEmail(data.email);
      track("signup_completed", { tier: (data?.tier as string) ?? "unknown" });
      track("magic_link_sent", { reason: "signup_finalize" });
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Cooldown ticker for the Resend button.
  useEffect(() => {
    if (cooldown <= 0) return;
    cooldownTimerRef.current = window.setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => {
      if (cooldownTimerRef.current) window.clearTimeout(cooldownTimerRef.current);
    };
  }, [cooldown]);

  const handleResend = async () => {
    if (!email || resending || cooldown > 0) return;
    setResending(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${window.location.origin}/dashboard`,
      },
    });
    setResending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    track("magic_link_sent", { reason: "signin" });
    toast.success("New sign-in link sent. Check your inbox.");
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  return (
    <div className="min-h-dvh flex items-center justify-center p-4 bg-background">
      <SeoHead title="Finishing your signup — Excerptus" description="Confirming your subscription and sending your magic link." />
      <Card className="w-full max-w-md shadow-elevated">
        <CardHeader>
          <h1 className="font-serif text-2xl font-semibold">
            {state === "loading" ? "Finishing your signup…" : state === "ok" ? "You're in" : "Something went wrong"}
          </h1>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === "loading" && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Confirming your card and creating your account…</span>
            </div>
          )}
          {state === "ok" && (
            <>
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertTitle>Card on file</AlertTitle>
                <AlertDescription>
                  We've sent a sign-in link to {email ? <strong>{email}</strong> : "your email"}. Click it to finish setting up your account — no password needed.
                </AlertDescription>
              </Alert>
              {email && (
                <Button
                  variant="outline"
                  onClick={handleResend}
                  disabled={resending || cooldown > 0}
                  className="w-full"
                >
                  {resending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                  ) : cooldown > 0 ? (
                    <>Resend available in {cooldown}s</>
                  ) : (
                    <><Mail className="mr-2 h-4 w-4" /> Resend sign-in link</>
                  )}
                </Button>
              )}
            </>
          )}
          {state === "error" && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Couldn't finish signup</AlertTitle>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Link to="/auth/"><Button variant="outline">Back to sign in</Button></Link>
            {state === "error" && <Link to="/pricing/"><Button>Try again</Button></Link>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthFinalize;
